import type {
  TenantDetailRaw,
  TenantDetailUI,
  TenantMember,
  TenantOwner,
  TenantProductConnection,
  TenantRaw,
  TenantRow,
} from "~/features/admin/tenants/types";
import { TENANT_MEMBERSHIP_ROLES, TENANT_PRODUCT_VISIBILITIES } from "~/features/admin/tenants/types";

/** API row → table row. */
function toTenantRow(raw: TenantRaw): TenantRow {
  return {
    id: raw.id,
    name: raw.name,
    shortCode: raw.shortCode,
    organization: raw.organization,
    ownerLabel: raw.ownerUser ? raw.ownerUser.displayName?.trim() || raw.ownerUser.email : null,
    updatedAt: raw.updatedAt,
    isDeactivated: Boolean(raw.deactivatedAt),
    installedProductIds: raw.tenantProductInstallations.map((i) => i.productId),
  };
}

export function toTenantRowList(raws: readonly TenantRaw[]): TenantRow[] {
  return raws.map(toTenantRow);
}

/** API detail row → detail-view model. */
export function toTenantDetailUI(raw: TenantDetailRaw): TenantDetailUI {
  const members: TenantMember[] = raw.userTenants.flatMap((ut) => {
    if (!ut.user) return [];
    return [
      {
        id: ut.user.id,
        displayName: ut.user.displayName ?? null,
        email: ut.user.email,
        deactivatedAt: ut.user.deactivatedAt ?? null,
        role: TENANT_MEMBERSHIP_ROLES.find((role) => role === ut.role) ?? "member",
      },
    ];
  });

  const owner: TenantOwner | null = raw.ownerUser
    ? {
        id: raw.ownerUser.id,
        // Blank display names fall back to the email in the UI.
        displayName: raw.ownerUser.displayName?.trim() || null,
        email: raw.ownerUser.email,
        deactivatedAt: raw.ownerUser.deactivatedAt ?? null,
      }
    : null;

  const productConnections: TenantProductConnection[] = raw.tenantProductInstallations.map((i) => ({
    id: i.id,
    productId: i.productId,
    productName: i.product.name,
    productDeactivatedAt: i.product.deactivatedAt ?? null,
    // The table column reads back as a plain string; unknown levels read as
    // private (fail-closed), same as the backend policy.
    visibility: TENANT_PRODUCT_VISIBILITIES.find((v) => v === i.visibility) ?? "private",
  }));

  return {
    id: raw.id,
    name: raw.name,
    shortCode: raw.shortCode,
    organizationId: raw.organizationId,
    organization: raw.organization,
    allowAutoApproveChangeRequests: raw.allowAutoApproveChangeRequests,
    isDeactivated: Boolean(raw.deactivatedAt),
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    deactivatedAt: raw.deactivatedAt ?? null,
    owner,
    members,
    productConnections,
  };
}
